/**
 * AdminMasterFileRow — single-row renderer for a master file in the Admin
 * Order Detail > Master section. Handles:
 *   • Filename, download
 *   • Per-file MD5 status badge (verified / mismatch / missing / pending)
 *   • Per-file "Regenerate MD5" admin action when the hash is missing
 *   • Rejected / Superseded / Replacement / Duplicate badges
 *   • Per-file Reject button (admin-only) that opens the rejection dialog
 *   • Rejection reason banner under the row
 *   • Duplicate-checksum detail line
 *
 * Used by both the multi-disc grouped view and the flat single-disc list.
 */
import { useState } from 'react';
import { Music, Download, AlertTriangle, XCircle, History, Check, RefreshCw, HelpCircle, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { filesApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Compute the MD5 badge meta from a file record. Centralised so it stays
 * consistent across the flat list, the disc-grouped view, and any future
 * surface that wants to render the same indicator.
 *
 * Buckets (priority top-down):
 *   • missing       — no `md5_checksum` stored. The file is "blind" — any
 *                     sidecar entry referencing it will stay `pending`
 *                     forever and the Master "Received" badge can never
 *                     light up until the hash is regenerated.
 *   • mismatch      — sidecar declared a hash but the computed one differs.
 *                     Confirmed corruption.
 *   • verified      — hash matches the sidecar manifest entry.
 *   • pending       — hash exists but no sidecar has claimed it yet (or
 *                     the sidecar's entry is still unresolved).
 */
function getMd5BadgeMeta(file) {
    if (!file) return null;
    // Sidecars themselves don't have/need their own MD5 — they ARE the
    // manifest. Show a distinct "Manifest" pill so the row still has a
    // verification signal but never offers a confusing "Regenerate MD5".
    if (file.is_checksum_file) {
        const summary = (file.checksum_summary || '').toLowerCase();
        if (summary === 'mismatch') {
            return {
                key: 'manifest-mismatch',
                label: 'Manifest mismatch',
                className: 'bg-rose-100 text-rose-800 border border-rose-200',
                icon: XCircle,
                title: 'Sidecar contains one or more declared hashes that do not match the uploaded master files.',
            };
        }
        if (summary === 'partial' || summary === 'pending') {
            return {
                key: 'manifest-pending',
                label: 'Manifest pending',
                className: 'bg-amber-100 text-amber-800 border border-amber-200',
                icon: AlertTriangle,
                title: 'One or more master files referenced by this manifest are still missing or have not been linked yet.',
            };
        }
        if (summary === 'match') {
            return {
                key: 'manifest-match',
                label: 'Manifest OK',
                className: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                icon: Check,
                title: 'All declared hashes in this manifest match the uploaded master files.',
            };
        }
        return {
            key: 'manifest',
            label: 'Manifest',
            className: 'bg-slate-100 text-slate-600 border border-slate-200',
            icon: HelpCircle,
            title: 'Checksum manifest sidecar.',
        };
    }
    if (!file.md5_checksum) {
        return {
            key: 'missing',
            label: 'Missing MD5',
            className: 'bg-rose-100 text-rose-800 border border-rose-200',
            icon: AlertTriangle,
            title: 'No MD5 hash stored for this file — sidecar verification will stay pending until the hash is regenerated.',
        };
    }
    if (file.md5_verified === 'mismatch') {
        return {
            key: 'mismatch',
            label: 'MD5 mismatch',
            className: 'bg-rose-100 text-rose-800 border border-rose-200',
            icon: XCircle,
            title: 'The computed hash does not match the value declared in the .md5 sidecar.',
        };
    }
    if (file.md5_verified === 'match') {
        // Prefer the FLOW-generated attribution over the generic
        // "MD5 verified" label whenever the verdict came from a
        // system-generated sidecar. Same green pill, clearer copy.
        if (file.md5_verified_by_flow_sidecar) {
            return {
                key: 'flow-generated',
                label: 'MD5 generated by FLOW',
                className: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                icon: Check,
                title: 'FLOW generated a .md5 sidecar for this master using its trusted server-computed hash.',
            };
        }
        return {
            key: 'verified',
            label: 'MD5 verified',
            className: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
            icon: Check,
            title: 'The computed hash matches the .md5 sidecar manifest.',
        };
    }
    return {
        key: 'pending',
        label: 'MD5 pending',
        className: 'bg-slate-100 text-slate-600 border border-slate-200',
        icon: HelpCircle,
        title: 'Hash is stored but no .md5 sidecar entry has claimed this file yet.',
    };
}

export function AdminMasterFileRow({
    file,
    onDownload,
    onReject,
    onRemove,
    onRestore,
    onFileUpdated,
    subLabel,
    'data-testid': dataTestId,
}) {
    const isRejected = !!file.rejected && !file.superseded;
    const isSuperseded = !!file.superseded;
    const isReplacement = !!file.replaces_file_id;
    const isSoftDeleted = !!file.soft_deleted;
    const hasDuplicates = (file.duplicate_master_matches || []).length > 0;
    const md5Badge = getMd5BadgeMeta(file);
    // Two separate per-file workflows:
    //   • Generate — only for files with NO hash stored. Streams from S3
    //     and computes the hash, then re-verifies.
    //   • Verify   — for files that DO have a hash but are stuck pending
    //     or showing mismatch against the sidecar, OR for sidecar files
    //     themselves (re-parses the manifest from S3).
    const showGenerate = md5Badge?.key === 'missing' && !isSuperseded;
    const showVerify = !isSuperseded && (
        md5Badge?.key === 'pending'
        || md5Badge?.key === 'mismatch'
        || md5Badge?.key === 'manifest-pending'
        || md5Badge?.key === 'manifest-mismatch'
    );
    const [generating, setGenerating] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const handleGenerate = async () => {
        if (generating) return;
        setGenerating(true);
        const toastId = toast.loading(`Generating MD5 for ${file.original_filename}…`);
        try {
            const res = await filesApi.regenerateFileMd5(file.id);
            toast.success(
                res.data?.md5_verified === 'match'
                    ? 'MD5 generated — verified against sidecar'
                    : res.data?.md5_verified === 'mismatch'
                        ? 'MD5 generated — mismatch against sidecar'
                        : 'MD5 generated',
                { id: toastId },
            );
            onFileUpdated?.({
                ...file,
                md5_checksum: res.data?.md5_checksum,
                md5_verified: res.data?.md5_verified,
                md5_verified_against_md5: res.data?.md5_verified_against_md5,
                md5_verified_against_file_id: res.data?.md5_verified_against_file_id,
            });
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || 'Failed to generate MD5',
                { id: toastId },
            );
        } finally {
            setGenerating(false);
        }
    };

    const handleVerify = async () => {
        if (verifying) return;
        setVerifying(true);
        const toastId = toast.loading(`Verifying ${file.original_filename}…`);
        try {
            const res = await filesApi.verifyFileMd5(file.id);
            const summary = res.data?.checksum_summary;
            const verified = res.data?.md5_verified;
            toast.success(
                verified === 'match' || summary === 'match'
                    ? 'Verified — match against sidecar'
                    : verified === 'mismatch' || summary === 'mismatch'
                        ? 'Verified — mismatch against sidecar'
                        : summary === 'partial'
                            ? 'Verified — manifest still partial'
                            : 'Verified — no matching sidecar entry yet',
                { id: toastId },
            );
            onFileUpdated?.({
                ...file,
                md5_verified: res.data?.md5_verified,
                md5_verified_against_md5: res.data?.md5_verified_against_md5,
                md5_verified_against_file_id: res.data?.md5_verified_against_file_id,
                checksum_summary: res.data?.checksum_summary,
            });
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || 'Failed to verify MD5',
                { id: toastId },
            );
        } finally {
            setVerifying(false);
        }
    };

    const rowClasses = isSoftDeleted
        ? 'bg-slate-50 border border-dashed border-slate-300 opacity-60'
        : isRejected
            ? 'bg-rose-50 border border-rose-300'
            : isSuperseded
                ? 'bg-slate-100 border border-slate-200 opacity-75'
                : md5Badge?.key === 'missing' || md5Badge?.key === 'mismatch'
                    ? 'bg-rose-50/60 border border-rose-200'
                    : hasDuplicates
                        ? 'bg-amber-50/70 border border-amber-200'
                        : 'bg-background';

    return (
        <div
            className={`flex flex-col gap-1 py-1.5 px-2 rounded text-xs ${rowClasses}`}
            data-testid={dataTestId || `master-file-row-${file.id}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Music className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    <span className={`truncate ${isSuperseded ? 'line-through text-slate-500' : ''}`}>
                        {file.original_filename}
                    </span>
                    {subLabel && (
                        <span className="text-slate-400 text-[10px]">({subLabel})</span>
                    )}
                    {md5Badge && (
                        <span
                            className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium ${md5Badge.className}`}
                            data-testid={`md5-badge-${md5Badge.key}-${file.id}`}
                            title={md5Badge.title}
                        >
                            <md5Badge.icon className="h-3 w-3" />
                            {md5Badge.label}
                        </span>
                    )}
                    {isRejected && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-rose-600 text-white border-0"
                            data-testid={`rejected-badge-${file.id}`}
                        >
                            <XCircle className="h-3 w-3" />
                            Rejected
                        </span>
                    )}
                    {isSuperseded && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-slate-200 text-slate-600 border-0"
                            title="Replaced by a newer version"
                        >
                            <History className="h-3 w-3" />
                            Superseded
                        </span>
                    )}
                    {isReplacement && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                            title={`Replacement for ${file.replaces_filename || 'rejected file'}`}
                        >
                            Replacement · pending review
                        </span>
                    )}
                    {hasDuplicates && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200"
                            data-testid={`duplicate-badge-${file.id}`}
                            title="Same checksum as a master file on another disc"
                        >
                            <AlertTriangle className="h-3 w-3" />
                            Duplicate
                        </span>
                    )}
                    {isSoftDeleted && (
                        <span
                            className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-slate-200 text-slate-700 border-0"
                            data-testid={`removed-badge-${file.id}`}
                            title={file.soft_delete_reason ? `Removed — ${file.soft_delete_reason}` : 'Removed by admin'}
                        >
                            <Trash2 className="h-3 w-3" />
                            Removed
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {showGenerate && !isSoftDeleted && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1"
                            onClick={handleGenerate}
                            disabled={generating}
                            title="Stream-download from S3 and compute MD5 server-side"
                            data-testid={`generate-md5-btn-${file.id}`}
                        >
                            <RefreshCw className={`h-3 w-3 ${generating ? 'animate-spin' : ''}`} />
                            {generating ? 'Generating…' : 'Generate MD5'}
                        </Button>
                    )}
                    {showVerify && !isSoftDeleted && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 gap-1"
                            onClick={handleVerify}
                            disabled={verifying}
                            title={
                                file.is_checksum_file
                                    ? 'Re-parse this manifest and re-link to master files'
                                    : 'Re-run sidecar matching for this file'
                            }
                            data-testid={`verify-md5-btn-${file.id}`}
                        >
                            <ShieldCheck className={`h-3 w-3 ${verifying ? 'animate-pulse' : ''}`} />
                            {verifying ? 'Verifying…' : 'Verify'}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => onDownload(file)}
                        title="Download"
                    >
                        <Download className="h-3 w-3" />
                    </Button>
                    {onReject && !isRejected && !isSuperseded && !isSoftDeleted && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => onReject(file)}
                            title="Reject file & request replacement"
                            data-testid={`reject-master-btn-${file.id}`}
                        >
                            <XCircle className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    {onRemove && !isSuperseded && !isSoftDeleted && (
                        // Trash icon is available on BOTH active AND
                        // already-rejected files. Removing a rejected
                        // file is a cleanup; the rejection audit stays
                        // in history, replacement_required relaxes.
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                            onClick={() => onRemove(file)}
                            title={
                                isRejected
                                    ? 'Remove rejected file (keeps rejection audit, no supplier status change)'
                                    : 'Remove unnecessary duplicate (soft delete — does NOT reject)'
                            }
                            data-testid={`remove-master-btn-${file.id}`}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    {onRestore && isSoftDeleted && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1"
                            onClick={() => onRestore(file)}
                            title="Restore this removed file"
                            data-testid={`restore-master-btn-${file.id}`}
                        >
                            <History className="h-3 w-3" />
                            Restore
                        </Button>
                    )}
                </div>
            </div>
            {isRejected && file.rejection_comment && (
                <div
                    className="ml-5 text-[11px] text-rose-800 bg-rose-100/60 border border-rose-200 rounded px-2 py-1 whitespace-pre-wrap"
                    data-testid={`rejection-comment-${file.id}`}
                >
                    <span className="font-semibold">Reason:</span> {file.rejection_comment}
                    {file.rejected_by_name && (
                        <span className="block mt-0.5 text-rose-500 text-[10px]">
                            — {file.rejected_by_name} · {new Date(file.rejected_at).toLocaleString()}
                        </span>
                    )}
                </div>
            )}
            {isSoftDeleted && (
                <div
                    className="ml-5 text-[11px] text-slate-600 bg-slate-100/70 border border-slate-200 rounded px-2 py-1"
                    data-testid={`removed-detail-${file.id}`}
                >
                    <span className="font-semibold">Removed</span>
                    {file.soft_delete_reason ? <> — {file.soft_delete_reason}</> : null}
                    {file.soft_deleted_by_name && (
                        <span className="block mt-0.5 text-slate-500 text-[10px]">
                            by {file.soft_deleted_by_name}
                            {file.soft_deleted_at && <> · {new Date(file.soft_deleted_at).toLocaleString()}</>}
                        </span>
                    )}
                    {(file.soft_delete_manifest_warnings || []).length > 0 && (
                        <div
                            className="mt-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-[10px]"
                            data-testid={`removed-manifest-warning-${file.id}`}
                        >
                            <AlertTriangle className="inline h-3 w-3 mr-1" />
                            Still referenced by{' '}
                            {file.soft_delete_manifest_warnings.map((w, i) => (
                                <span key={w.sidecar_id}>
                                    {i > 0 ? ', ' : ''}
                                    <span className="font-mono">{w.sidecar_filename || w.sidecar_id}</span>
                                </span>
                            ))}
                            . Review manifest consistency.
                        </div>
                    )}
                </div>
            )}
            {hasDuplicates && (
                <div
                    className="ml-5 text-[11px] text-amber-700"
                    data-testid={`duplicate-detail-${file.id}`}
                >
                    Same checksum as{' '}
                    {file.duplicate_master_matches.map((m, i) => (
                        <span key={m.file_id}>
                            {i > 0 && (i === file.duplicate_master_matches.length - 1 ? ' and ' : ', ')}
                            <span className="font-semibold">{m.disc_label}</span>
                            {m.filename && (
                                <> / <span className="font-mono">{m.filename}</span></>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminMasterFileRow;
